import { useCallback, useEffect, useRef } from 'react';

import { useDispatch } from 'react-redux';

import { setFullscreen } from 'store/epg/actions';

import { ORIENTATION } from 'utils/orientation';
import { isWeb } from 'utils/responsiveness';

import {
    enterWebFullscreen,
    exitWebFullscreen,
    onAfterHidePlayerModal,
    onBeforeShowPlayerModal,
} from 'screens/PlayerModal/utils';

type OrientationParameter = ORIENTATION | undefined;
type Params = {
    focused: boolean;
    webPlayerElementId: string;
    isDatePickerListVisible: boolean;
    isFilterListVisible: boolean;
    onCloseDatePicker(): void;
    onCloseFilterList(): void;
};

function useShowFullscreenPlayer({
    focused,
    webPlayerElementId,
    isDatePickerListVisible,
    isFilterListVisible,
    onCloseDatePicker,
    onCloseFilterList,
}: Params) {
    const dispatch = useDispatch();

    const actualOrientation = useRef<OrientationParameter>(undefined);

    const handleShowFullscreenPlayer = useCallback(
        (orientation: OrientationParameter) => {
            onBeforeShowPlayerModal(orientation);
            dispatch(setFullscreen(true));
        },
        [],
    );

    const onBottomSheetDismiss = useCallback(() => {
        if (actualOrientation.current) {
            handleShowFullscreenPlayer(actualOrientation.current);
            actualOrientation.current = undefined;
        }
    }, [handleShowFullscreenPlayer]);

    const onShowFullscreenPlayer = useCallback(
        async (orientation: OrientationParameter) => {
            if (isWeb) {
                await enterWebFullscreen(webPlayerElementId);
                dispatch(setFullscreen(true));
            } else if (isDatePickerListVisible) {
                onCloseDatePicker();
                actualOrientation.current = orientation;
            } else if (isFilterListVisible) {
                onCloseFilterList();
                actualOrientation.current = orientation;
            } else {
                handleShowFullscreenPlayer(orientation);
            }
        },
        [
            webPlayerElementId,
            handleShowFullscreenPlayer,
            isDatePickerListVisible,
            isFilterListVisible,
            onCloseDatePicker,
            onCloseFilterList,
        ],
    );

    const onExitFullscreenPlayer = useCallback(async () => {
        if (isWeb) {
            await exitWebFullscreen();
        } else {
            onAfterHidePlayerModal();
        }

        dispatch(setFullscreen(false));
    }, []);

    const handleFullscreenChange = useCallback(() => {
        dispatch(setFullscreen(!!document.fullscreenElement));
    }, []);

    useEffect(() => {
        if (isWeb) {
            if (focused) {
                document.addEventListener(
                    'fullscreenchange',
                    handleFullscreenChange,
                );
            } else {
                document.removeEventListener(
                    'fullscreenchange',
                    handleFullscreenChange,
                );
            }
        }
    }, [focused]);

    return {
        onShowFullscreenPlayer,
        onExitFullscreenPlayer,
        onBottomSheetDismiss,
    };
}

export default useShowFullscreenPlayer;
